import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTileHighlightBanner from '../shared/SearchTileHighlightBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { hasPassedWalledGarden } from 'selectors/accountSelectors'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import { EmptyObject } from 'lib/object/objectUtils'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import {
  useHighlightMessages,
} from 'components/OfferList/OfferListTiles/constants'
import { buildSearchParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { OfferUnavailableReason } from 'lib/search/constants'
import { dateDifference } from 'lib/datetime/dateUtils'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  filters?: App.OfferListFilters
}

function HotelSearchTileHighlightBanner({ offer, offerMetaData, filters = EmptyObject }: Props) {
  const intl = useIntl()
  const { highlightMessageAvailableForDates, highlightMessageStayMinNights, highlightMessageUnavailableForDates } = useHighlightMessages()
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)

  const greatDealBookNow = intl.formatMessage({
    id: 'ixCt2Xm_ph7W6kUbgy1d',
    defaultMessage: 'We found a great deal for your hotel. Book now to secure your room!',
    description: 'Highlight banner for escape/always on offers when search pricing is available',
  })
  const limitedTimeDeal = intl.formatMessage({
    id: 'ghyYSPa6xNSk0AZyNzJq',
    defaultMessage: 'We found a limited time deal for your hotel!',
    description: 'Highlight banner for flash hotel offers when search pricing is available',
  })
  const greatDealChooseDates = intl.formatMessage({
    id: 'GwFsPZSkY-0fYPl5J4w0',
    defaultMessage: 'We found a great deal for your hotel. Choose dates and book now to secure your room!',
    description: 'Highlight banner for escape/always on offers prompting user to choose dates',
  })
  const limitedTimeDealChooseDates = intl.formatMessage({
    id: '6mTMF9sUNluGuNLSLlqp',
    defaultMessage: 'We found a limited time deal for your hotel. Choose dates and book now before it ends!',
    description: 'Highlight banner for flash hotel offers prompting user to choose dates',
  })

  const bookablePropertyResultMessage = useMemo<Record<string, string>>(() => ({
    [OFFER_TYPE_ALWAYS_ON]: greatDealBookNow,
    [OFFER_TYPE_HOTEL]: limitedTimeDeal,
  }), [greatDealBookNow, limitedTimeDeal])

  const bookablePropertyResultChooseDatesMessage = useMemo<Record<string, string>>(() => ({
    [OFFER_TYPE_ALWAYS_ON]: greatDealChooseDates,
    [OFFER_TYPE_HOTEL]: limitedTimeDealChooseDates,
  }), [greatDealChooseDates, limitedTimeDealChooseDates])
  const passedWalledGarden = useAppSelector(hasPassedWalledGarden)
  const checkIn = filters?.checkIn ?? offerMetaData?.suggestedTravelDates?.checkIn
  const checkOut = filters?.checkOut ?? offerMetaData?.suggestedTravelDates?.checkOut
  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, filters?.rooms), [checkIn, checkOut, filters?.rooms])
  const bestRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)

  const spoofBannerText = intl.formatMessage({
    id: 'uJF4ZkKxILI4Em-jniKK',
    defaultMessage: 'This hotel is only visible to staff and available for purchase.',
    description: 'Spoof mode banner indicating hotel is only visible to internal staff',
  })

  const spoofMessage = useMemo<string | undefined>(() => {
    if (isSpoofed && !scheduleIsCurrent(offer.visibilitySchedule)) {
      return spoofBannerText
    }
  }, [isSpoofed, offer, spoofBannerText])

  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })
  const shouldUseBestPrice = (passedWalledGarden || !offer.walledGarden) && !!(checkIn && checkOut && filters?.rooms)
  const isSearchAvailable = directSearchPrices && offerMetaData?.available
  const fetchingPrice = shouldUseBestPrice && !directSearchPrices && (!bestRate && !bestPriceError)
  const available = bestRate?.available || offerMetaData?.available

  const nights = useMemo(() => {
    if (checkIn && checkOut) {
      return dateDifference(new Date(checkOut), new Date(checkIn)).days
    }
    return null
  }, [checkIn, checkOut])

  const description = useMemo<string | undefined>(() => {
    if (spoofMessage) return spoofMessage
    if (shouldUseBestPrice) {
      if (isSearchAvailable) {
        // search pricing is available, and the offer is available
        return bookablePropertyResultMessage[offer.type]
      } else if (
        !offerMetaData?.available &&
        nights &&
        offer.minDuration &&
        offer.minDuration > nights
      ) {
        // search has found the offer to be unavailable due to min nights not satisfied
        return highlightMessageStayMinNights(offer.minDuration)
      } else if (directSearchPrices && !offerMetaData?.available) {
        // search has found the offer to be unavailable
        return highlightMessageUnavailableForDates(offer.saleUnit)
      } else if (fetchingPrice) {
        return undefined
      } else if (bestRate?.available && !bestPriceError) {
        return bookablePropertyResultMessage[offer.type]
      } else {
        return highlightMessageUnavailableForDates(offer.saleUnit)
      }
    }
    if (offerMetaData?.unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED) { // capacity exceeded
      return intl.formatMessage(
        {
          id: 'UXJuV7Zpl8PbiIac10TA',
          defaultMessage: 'The number of guests exceeds the {saleUnit}’s maximum capacity.',
          description: 'Highlight banner when guest count exceeds the sale unit capacity',
        },
        { saleUnit: offer.saleUnit },
      )
    }
    if (offerMetaData?.unavailableReason === OfferUnavailableReason.DATE_UNAVAILABLE) { // date unavailable
      return highlightMessageUnavailableForDates(offer.saleUnit)
    }
    if (checkIn && checkOut && filters?.rooms) { // has enough filters
      if (shouldUseRealSearchPrices && !offerMetaData?.available) { // search has checked dates and found no availability
        return highlightMessageUnavailableForDates(offer.saleUnit)
      }
      if (shouldUseRealSearchPrices && offerMetaData?.available) { // search has checked dates and found availability
        return highlightMessageAvailableForDates
      }
      if (available || bestPriceError) {
        return highlightMessageAvailableForDates
      }
      if (!offer.isSoldOut && offer.minDuration && nights && offer.minDuration > nights) {
        return highlightMessageStayMinNights(offer.minDuration)
      } else {
        return highlightMessageUnavailableForDates(offer.saleUnit)
      }
    }
    // else {
    //   return 'We found a limited time deal for your hotel. Choose dates and book now before it ends!'
    // }

    return passedWalledGarden ?
      bookablePropertyResultChooseDatesMessage[offer.type] :
      bookablePropertyResultMessage[offer.type]
  }, [available, bestPriceError, bestRate, bookablePropertyResultChooseDatesMessage, bookablePropertyResultMessage, checkIn, checkOut, directSearchPrices, fetchingPrice, filters, highlightMessageAvailableForDates, highlightMessageStayMinNights, highlightMessageUnavailableForDates, intl, isSearchAvailable, nights, offer.isSoldOut, offer.minDuration, offer.saleUnit, offer.type, offerMetaData?.available, offerMetaData?.unavailableReason, passedWalledGarden, shouldUseRealSearchPrices, shouldUseBestPrice, spoofMessage])

  if (!description) return null

  return <SearchTileHighlightBanner description={description} isSpoof={!!spoofMessage} />
}

export default HotelSearchTileHighlightBanner
